<template>
  <div class="to-print mt-n15 mt-10">
    <h2 class="title__principal mb-5">
      {{ $t('appointment.print.questionnaireTitle', { code: appointment.code }) }}
    </h2>

    <p class="mb-0"><strong>{{ $t('appointment.print.doneDate') }}</strong></p>
    <p class="mb-1">{{ action(appointment.history) }}: {{ date(appointment.history) }} | {{
      formatExternalCode(appointment.external_code) }}</p>

    <v-row class="mb-1">
      <v-col>
        <p class="mb-0">
          <strong>{{ $t('appointment.print.host') }}</strong>
        </p>
        <span v-html="$t('appointment.print.hostInfo', {
          host: hostName,
          hostEmail: hostEmail,
        })" />
      </v-col>
      <v-col v-if="appointment.visitor_name">
        <p class="mb-0">
          <strong v-if="isVisit">{{ $t('appointment.print.visitor') }}</strong>
          <strong v-else>{{ $t('appointment.print.tenant') }}</strong>
        </p>
        <span v-html="$t('appointment.print.visitorInfo', {
          visitor: appointment.visitor_name,
          visitorEmail: appointment.visitor_mail,
          visitorPhone: formatPhone(appointment.visitor_phone),
        })" />
      </v-col>
    </v-row>

    <p class="mb-0"><strong>{{ $t('appointment.print.realty') }}</strong></p>
    <p v-html="listRealties(appointment.realties)" />

    <div v-if="hasQuestionnaires">
      <span v-for="item in questionnaire" :key="item.id">
        <h3 class="mb-3">
          <strong>{{ $t('appointment.print.questionnaire') }} {{ item.title }}</strong>
        </h3>
        <div class="question" v-for="question in item.questions" :key="question.id">
          <p v-if="question.label" class="mb-0">
            <strong>{{ question.label }}</strong>
          </p>
          <p v-if="question.answer" class="mb-5">
            {{ question.answer }}
          </p>
          <div v-if="question.image" class="media-wrapper mb-5">
            <img v-if="imageTypes.includes(question.image.type)" :src="question.image.url" class="image" />
            <img :class="`placeholder ${question.image.type}`" :src="placeholderAsset" />
            <small class="label d-block pt-2 pl-2 pr-2 pb-2 text-center">
              {{ question.label }}
            </small>
          </div>
        </div>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { catalogDate, catalogPhone } from '@/util/catalog'
import { getDownloadFileAnswerUrl } from '@/services/questionnaire/questionnaireService'
import placeholderAsset from '@/assets/images/placeholder-media.jpeg'

import { visitTypeServices } from '../../appointment/formFieldsCreate/appointmentFormDefaults'


export default {
  name: 'PrintOnlyQuestionnaire',

  computed: {
    ...mapGetters(['appointment', 'appointmentQuestionnaires']),

    hasQuestionnaires() {
      return this.appointmentQuestionnaires?.length >= 0
    },

    isVisit() {
      return visitTypeServices.includes(this.appointment.type)
    },

    hostName() {
      return this.appointment?.host?.name
    },

    hostEmail() {
      return this.appointment?.host?.username
    },
  },

  methods: {
    listRealties(realties) {
      if (!realties) return
      if (realties.length <= 0) return
      return `
        ${realties.map((realty) => `
          <p class="mb-0">${realty.code}</p>
          <p>
            <small>
              ${realty.address}
            </small>
          </p>
        `).join(', ')}
      `
    },

    getFileType(url) {
      if (!url) return
      const fileType = url.slice((Math.max(0, url.lastIndexOf('.')) || Infinity) + 1)?.split('?')
      if (fileType.length <= 0) return
      return fileType[0]
    },

    formatExternalCode(externalCode) {
      if (!externalCode) return this.$t('appointment.print.noExternalCode')
      return externalCode
    },

    formatDate(date) {
      return catalogDate(date)
    },

    formatPhone(phone) {
      return catalogPhone(phone)
    },

    hasQuestions(questions) {
      if (!questions) return
      if (questions.length <= 0) return
      return true
    },

    handleAnswer(question) {
      let answer = question.answer
      switch (question.type) {
        case 'BOOLEAN':
          return this.$t(`boolean.${answer.bool_value}`)
        case 'NUMERIC':
          return answer.numeric_value
        case 'ENUM':
          return answer.enum_value
        case 'TEXT':
          return answer.text_value
        default:
          return null
      }
    },

    handleHistory(history) {
      if (!history) return
      if (history.length <= 0) return

      const filteredHistory = history.filter((item) => item.enum_action === 'APPOINTMENT_FINISHED')
      const parsedHistory = {
        action: filteredHistory[0]?.action,
        date: this.formatDate(filteredHistory[0]?.date_created),
      }

      return parsedHistory
    },

    action(history) {
      if (!this.handleHistory(history)?.action) return '---'
      return this.handleHistory(history).action
    },

    date(history) {
      if (!this.handleHistory(history)?.date) return '---'
      return this.handleHistory(history).date
    },

    async getFileURL(answerId) {
      try {
        const response = await getDownloadFileAnswerUrl(answerId, this.isAdmin)
        return {
          url: response.data.url,
          type: this.getFileType(response.data.url)
        }
      } catch (error) {
        return 'ERROR'
      }
    },

    async parseQuestions(questions) {
      const parsedQuestions = []

      await questions.forEach(async (question) => {
        try {
          if (question.answer) {
            if ((question.type !== 'FILE')) {
              parsedQuestions.push({
                id: question.id,
                label: question.label,
                answer: this.handleAnswer(question)
              })
            } else {
              parsedQuestions.push({
                id: question.id,
                label: question.label,
                image: await this.getFileURL(question.answer.id)
              })
            }
          } else {
            parsedQuestions.push({ answer: '---' })
          }
        } catch (error) {
          parsedQuestions.push({ answer: '---' })
        }
      })

      return parsedQuestions
    },
  },

  mounted() {
    if (this.hasQuestionnaires) {
      this.appointmentQuestionnaires.forEach(async (questionnaire) => {
        this.questionnaire.push({
          id: questionnaire.id,
          title: questionnaire.name,
          date: this.formatDate(questionnaire.date_created),
          questions: await this.parseQuestions(questionnaire.questions),
        })
      })
    }
  },

  data() {
    return {
      placeholderAsset,
      questionnaire: [],
      imageTypes: ['png', 'jpg', 'jpeg', 'gif', 'PNG', 'JPG', 'JPEG', 'GIF'],
    }
  },
}
</script>

<style scoped>
.question {
  page-break-inside: avoid;
}

.media-wrapper {
  position: relative;
  page-break-inside: avoid;
  max-width: 350px;
  min-height: 200px;
  border-radius: 6px;
  background-color: #f5f5f5;
  overflow: hidden;
}

.label {
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 2;
  border: 1px solid #eee;
  border-radius: 0 0 6px 6px;
  background-color: rgba(255, 255, 255, 0.8);
}

.image {
  max-width: 350px;
  line-height: 0;
  top: 0;
  left: 0;
  margin: 0;
}

.placeholder {
  display: block;
  position: absolute;
  page-break-inside: avoid;
  width: 350px;
  overflow: hidden;
  top: 0;
  left: 0;
  margin: 0;
  z-index: 0;
  line-height: 0;
}

.image {
  position: relative;
  z-index: 1;
}

.placeholder.mp3 {
  top: -10px;
}

.placeholder.mp4,
.placeholder.mov,
.placeholder.avi {
  top: -230px;
}

.placeholder.png,
.placeholder.jpg,
.placeholder.jpeg,
.placeholder.gif {
  top: -454px;
}

.icon {
  opacity: 0.5;
}

@media screen {
  .to-print {
    display: none;
  }

  hr {
    border: 0;
    border-top: 1px solid #ddd;
  }
}

@media print {
  .print__logo {
    display: none !important;
  }

  .to-print {
    display: block;
    font-size: 14px;
  }

  hr {
    border: 0;
    border-top: 1px solid #ddd;
  }
}
</style>
